import { useState, useEffect } from 'react'
import { X } from '@/ui/icons'
import { Button } from '@/ui/components'
import { Input } from '@/ui/components'
import { Textarea } from '@/ui/components'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/components'
import { FormField, FormSection } from '@/ui/components'
import { VehicleProfile, UpdateVehicleProfileDto } from '@/shared/api/vehicles'
import { useVehicles } from '../hooks/useVehicles'

interface VehicleEditModalProps {
  isOpen: boolean
  onClose: () => void
  /** The vehicle to edit. Leave it out to add a new one. */
  vehicle?: VehicleProfile
  onSaved?: (vehicle: VehicleProfile) => void
}

function toFormData(vehicle?: VehicleProfile): UpdateVehicleProfileDto {
  return {
    name: vehicle?.name || '',
    vehicle_type: vehicle?.vehicle_type ?? 'truck',
    make: vehicle?.make ?? '',
    model: vehicle?.model ?? '',
    year: vehicle?.year,
    notes: vehicle?.notes || '',
    is_daily_driver: vehicle ? vehicle.is_daily_driver : true,
    has_winch: vehicle?.has_winch || false,
    has_skid_plates: vehicle?.has_skid_plates || false,
    has_rock_sliders: vehicle?.has_rock_sliders || false,
    has_roof_rack: vehicle?.has_roof_rack || false,
    has_roof_top_tent: vehicle?.has_roof_top_tent || false,
    has_truck_camper: vehicle?.has_truck_camper || false
  }
}

const MIN_YEAR = 1900
const MAX_YEAR = new Date().getFullYear() + 1

export function VehicleEditModal({
  isOpen,
  onClose,
  vehicle,
  onSaved
}: VehicleEditModalProps) {
  const { createVehicle, updateVehicle } = useVehicles()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<UpdateVehicleProfileDto>(() => toFormData(vehicle))

  // Reopening starts from the chosen vehicle, or from a blank form when adding.
  useEffect(() => {
    if (isOpen) {
      setFormData(toFormData(vehicle))
      setError(null)
    }
  }, [vehicle, isOpen])

  const handleInputChange = (field: keyof UpdateVehicleProfileDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const make = formData.make?.trim()
    const model = formData.model?.trim()

    if (!make) {
      setError('Make is required')
      return
    }
    if (!model) {
      setError('Model is required')
      return
    }
    if (formData.year !== undefined && (formData.year < MIN_YEAR || formData.year > MAX_YEAR)) {
      setError(`Year must be between ${MIN_YEAR} and ${MAX_YEAR}`)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const name = formData.name?.trim()
      const saved = vehicle
        ? await updateVehicle(vehicle.id, { ...formData, make, model, name: name || null })
        : await createVehicle({
            ...formData,
            name: name || undefined,
            vehicle_type: formData.vehicle_type ?? 'truck',
            make,
            model
          })
      onSaved?.(saved)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : "The vehicle didn't save. Try again.")
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="vehicle-editor-title"
        className="bg-card rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-border shadow-lg"
      >
        <div className="sticky top-0 z-10 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 id="vehicle-editor-title" className="text-base font-semibold text-foreground">
            {vehicle ? 'Edit vehicle' : 'Add a vehicle'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isLoading} aria-label="Close" className="shrink-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          <FormSection title="Vehicle details">
            <FormField
              label="Nickname"
              htmlFor="name"
              hint="Optional — if blank, we'll use make and model."
            >
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Desert Fox, Weekend Warrior"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Make" htmlFor="make" required>
                <Input
                  id="make"
                  value={formData.make || ''}
                  onChange={(e) => handleInputChange('make', e.target.value)}
                  placeholder="e.g., Toyota"
                  required
                />
              </FormField>
              <FormField label="Model" htmlFor="model" required>
                <Input
                  id="model"
                  value={formData.model || ''}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  placeholder="e.g., Tacoma"
                  required
                />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Year" htmlFor="year">
                <Input
                  id="year"
                  type="number"
                  value={formData.year || ''}
                  onChange={(e) => handleInputChange('year', e.target.value ? parseInt(e.target.value) : undefined)}
                  min={MIN_YEAR}
                  max={MAX_YEAR}
                />
              </FormField>
              <FormField label="Vehicle type" htmlFor="vehicle_type">
                <Select
                  value={formData.vehicle_type}
                  onValueChange={(value) => handleInputChange('vehicle_type', value)}
                >
                  <SelectTrigger id="vehicle_type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="suv">SUV</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <div className="flex items-center space-x-2 pt-1">
              <input
                id="is_daily_driver"
                type="checkbox"
                checked={!!formData.is_daily_driver}
                onChange={(e) => handleInputChange('is_daily_driver', e.target.checked)}
                className="rounded border-border text-adventure-500 focus:ring-adventure-500"
              />
              <label htmlFor="is_daily_driver" className="text-sm font-medium text-foreground cursor-pointer">
                Daily driver
              </label>
            </div>

            <FormField label="Notes" htmlFor="notes" hint="Only you can see these.">
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Quirks, maintenance, anything to remember"
                rows={3}
              />
            </FormField>
          </FormSection>

          <FormSection title="Equipment" description="Check everything that's currently on your rig.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { field: 'has_winch', label: 'Winch' },
                { field: 'has_skid_plates', label: 'Skid plates' },
                { field: 'has_rock_sliders', label: 'Rock sliders' },
                { field: 'has_roof_rack', label: 'Roof rack' },
                { field: 'has_roof_top_tent', label: 'Roof top tent' },
                { field: 'has_truck_camper', label: 'Truck camper' },
              ].map(({ field, label }) => (
                <label key={field} className="flex items-center gap-3 text-sm font-medium text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData[field as keyof UpdateVehicleProfileDto]}
                    onChange={(e) => handleInputChange(field as keyof UpdateVehicleProfileDto, e.target.checked)}
                    className="rounded border-border text-adventure-500 focus:ring-adventure-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </FormSection>

          {error && (
            <div className="bg-error/10 border border-error/30 rounded-md p-3">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-2 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save vehicle'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
